<template>
  <!-- Case 1: null or empty string => "-" -->
  <template v-if="isEmpty"> - </template>

  <!-- Case 2: Array of reference items => horizontal flex display -->
  <template v-else-if="isArray">
    <div v-if="containsReferenceItems" class="reference-items-container">
      <template v-for="(item, index) in props.value" :key="index">
        <!-- Location item -->
        <router-link
          v-if="isLocationItem(item)"
          :to="`/locations/${item.location_id}`"
          class="badge text-bg-secondary reference-item shadow-sm"
        >
          {{ item.location_name }}
        </router-link>

        <!-- Software channel item -->
        <router-link
          v-else-if="isSoftwareChannelItem(item)"
          :to="`/channels/${item.sw_channel_id}`"
          class="badge text-bg-secondary reference-item shadow-sm"
        >
          {{ item.sw_channel_name }}
        </router-link>

        <!-- Organization item -->
        <router-link
          v-else-if="isOrganizationItem(item)"
          :to="`/organizations/${item.organization_id || item.id}`"
          class="badge text-bg-secondary reference-item shadow-sm"
        >
          {{ item.organization_name || item.name }}
        </router-link>

        <!-- Regular value -->
        <span v-else class="reference-item">{{ item }}</span>
      </template>
    </div>

    <!-- Regular array of primitive values -->
    <template v-else>
      {{ formattedArray }}
    </template>
  </template>

  <!-- Case 3: LocationItem => router-link to /locations/:location_id -->
  <template v-else-if="isLocationItem(value)">
    <router-link :to="`/locations/${value.location_id}`">
      {{ value.location_name }}
    </router-link>
  </template>

  <!-- Case 4: SoftwareChannelItem => router-link to /software-channels/:sw_channel_id -->
  <template v-else-if="isSoftwareChannelItem(value)">
    <router-link :to="`/channels/${value.sw_channel_id}`">
      {{ value.sw_channel_name }}
    </router-link>
  </template>

  <!-- Case 5: OrganizationItem => router-link to /organizations/:organization_id -->
  <template v-else-if="isOrganizationItem(value)">
    <router-link :to="`/organizations/${value.organization_id || value.id}`">
      {{ value.organization_name || value.name }}
    </router-link>
  </template>

  <!-- Case 6: Plain string/number -->
  <template v-else>
    {{ value }}
  </template>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  isLocationItem,
  isSoftwareChannelItem,
  isOrganizationItem,
  ValueType,
} from "./DetailTableTypes";

interface Props {
  value: ValueType;
}

const props = defineProps<Props>();

const isEmpty = computed(() => props.value === null || props.value === "");
const isArray = computed(() => Array.isArray(props.value));

const containsReferenceItems = computed(() => {
  if (!isArray.value) return false;
  const arr = props.value as ValueType[];
  return arr.some(
    (item) =>
      isLocationItem(item) ||
      isSoftwareChannelItem(item) ||
      isOrganizationItem(item)
  );
});

const formattedArray = computed(() => {
  if (!isArray.value) return "";
  return (props.value as (string | number)[]).join(", ");
});
</script>

<style scoped>
.reference-items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reference-item {
  padding: 8px 8px;
  font-size: 0.9em;
  text-decoration: none;
}
</style>
